import React from 'react';
import { ChevronLeft, ChevronRight, Maximize, Minimize, Heart, List } from 'lucide-react';
import Switch from './Switch';

interface ControlsOverlayProps {
    isVisible: boolean;
    title: string;
    duration: string;
    isAutoplay: boolean;
    setIsAutoplay: (val: boolean) => void;
    isFavorite: boolean;
    setIsFavorite: (val: boolean) => void;
    toggleFullscreen: () => void;
    isFullscreen: boolean;
    onNext?: () => void;
    onPrev?: () => void;
    onToggleContent?: () => void; // Nuevo prop
}

const ControlsOverlay: React.FC<ControlsOverlayProps> = ({
    isVisible,
    title,
    duration,
    isAutoplay,
    setIsAutoplay,
    isFavorite,
    setIsFavorite,
    toggleFullscreen,
    isFullscreen,
    onNext,
    onPrev,
    onToggleContent,
}) => {
    return (
        <div
            className={`absolute inset-x-0 top-0 transition-all duration-500 ease-in-out transform ${
                isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
            } z-50`}
        >
            {/* Background Gradient for visibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/40 to-transparent h-40 md:h-56 -z-10" />

            <div className="flex items-center justify-between px-3 md:px-8 py-3 md:py-6 backdrop-blur-md bg-black/10 border-b border-white/5">
                {/* Left Section */}
                <div className="flex items-center gap-1.5 md:gap-4 flex-1">
                    <ControlButton icon={<ChevronLeft size={18} />} label="Anterior" hideLabelMobile onClick={onPrev} />

                    <div className="hidden sm:block">
                        <ControlButton
                            icon={<List size={18} />}
                            label="Contenido"
                            hideLabelMobile
                            onClick={onToggleContent}
                        />
                    </div>

                    <div className="flex items-center gap-2 ml-1 md:ml-4 shrink-0">
                        <span className="hidden lg:inline text-[10px] font-bold text-white/50 uppercase tracking-widest">
                            Auto
                        </span>
                        <Switch checked={isAutoplay} onChange={setIsAutoplay} />
                    </div>
                </div>

                {/* Center Section - Dynamic Title */}
                <div className="flex flex-col items-center text-center px-2 flex-[2] max-w-[45%] md:max-w-xl">
                    <h1 className="text-xs md:text-lg font-bold text-white truncate w-full tracking-tight leading-tight">
                        {title}
                    </h1>
                    <span className="text-[9px] md:text-xs text-indigo-400 font-bold uppercase tracking-tighter md:tracking-widest mt-0.5">
                        {duration}
                    </span>
                </div>

                {/* Right Section */}
                <div className="flex items-center justify-end gap-1.5 md:gap-4 flex-1">
                    <button
                        title="favorite"
                        onClick={() => setIsFavorite(!isFavorite)}
                        className={`p-2 rounded-full border transition-all duration-300 ${
                            isFavorite
                                ? 'bg-red-500/20 border-red-500/50 text-red-500'
                                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                        }`}
                    >
                        <Heart size={18} className={isFavorite ? 'fill-current' : ''} />
                    </button>

                    <div className="hidden md:block">
                        <ControlButton
                            icon={isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                            onClick={toggleFullscreen}
                            label={isFullscreen ? 'Salir' : 'Expandir'}
                        />
                    </div>

                    {/* Icon-only fullscreen for mobile */}
                    <button
                        onClick={toggleFullscreen}
                        className="md:hidden p-2 rounded-full bg-white/5 text-white border border-white/10 hover:bg-white/10"
                    >
                        {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                    </button>

                    <ControlButton
                        icon={<ChevronRight size={18} />}
                        label="Siguiente"
                        secondary
                        hideLabelMobile
                        onClick={onNext}
                    />
                </div>
            </div>
        </div>
    );
};

interface ControlButtonProps {
    icon: React.ReactNode;
    label?: string;
    onClick?: () => void;
    secondary?: boolean;
    hideLabelMobile?: boolean;
}

const ControlButton: React.FC<ControlButtonProps> = ({ icon, label, onClick, secondary, hideLabelMobile }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-2.5 md:px-5 py-2 md:py-2.5 rounded-full transition-all duration-200 border border-white/10 hover:border-white/30 hover:scale-105 active:scale-95 ${
            secondary ? 'bg-white text-black' : 'bg-white/5 text-white'
        }`}
    >
        {icon}
        {label && (
            <span
                className={`text-[11px] md:text-sm font-bold uppercase tracking-wider ${hideLabelMobile ? 'hidden md:inline' : 'inline'}`}
            >
                {label}
            </span>
        )}
    </button>
);

export default ControlsOverlay;
